import React from 'react';

import { Box, Typography } from '@mui/material';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Lottie from 'lottie-react';

import hourGlassAnimation from '../assets/animations/hourglass.json';
import { environment } from '../config';
import { OfferCategory, OfferRequirements, Reservation, ReservationStatus } from '../graphql/API';
import { getReservationRequirementsValues } from '../utils/reservations';
import LoadingButton from './Button/LoadingButton';
import { ExtendedOfferRequirements } from './Dialogs/CreateProductInvite/context/types';
import { StatusLabelV2 } from './StatusLabelV2/StatusLabelV2';

dayjs.extend(utc);

const ReservationPostTypePostingTimeLimitInHours: Record<string, number> = {
  [OfferRequirements.postOnInstagramFeed]: 72,
  [ExtendedOfferRequirements.noRequirement]: 0,
  [OfferRequirements.postOnInstagramReels]: 72,
  [OfferRequirements.postOnInstagramStories3x]: 12,
  [OfferRequirements.postOnInstagramStories]: 12,
  [OfferRequirements.postOnTikTokVideo]: 72,
};

type Props = {
  data: Reservation;
  showExtendBtn?: boolean;
  isExtendButtonLoading?: boolean;
  onExtendHandle?: () => void;
};

export const ReservationTimeLeftToPost: React.FC<Props> = ({
  data,
  showExtendBtn = false,
  isExtendButtonLoading = false,
  onExtendHandle,
}) => {
  const isProductCategory = data?.offerCategory === OfferCategory.product;
  const requirementsValues = getReservationRequirementsValues(data);

  const renderExtendButton = React.useCallback(() => {
    if (showExtendBtn) {
      return (
        <LoadingButton
          variant='outlined'
          size='small'
          sx={{ borderRadius: '8px', border: '1.5px solid #000', p: '4px 16px' }}
          onClick={onExtendHandle}
          loading={isExtendButtonLoading}
        >
          Extend
        </LoadingButton>
      );
    }
  }, [showExtendBtn, onExtendHandle, isExtendButtonLoading]);

  const timeLeftToPost = React.useMemo(() => {
    if (data?.status === ReservationStatus.checkedin && data?.shippedDate && isProductCategory) {
      const targetDate = dayjs.utc(data?.effectiveEndDateTime)
      const now = dayjs.utc()
      const diffInHours = targetDate.diff(now, 'hour');
      const diffInDays = targetDate.diff(now, 'day');

      if (diffInHours <= 0 && diffInDays <= 0 && !data?.requirementsSubmitted)
        return <StatusLabelV2 type='promotionStatus' status={'notCompleted'} />;

      if (data?.requirementsSubmitted)
        return <StatusLabelV2 type='promotionStatus' status={'completed'} />;

      if (diffInDays > 1)
        return (
          <Box display='flex' alignItems='center' gap={'16px'} width='100%'>
            <Box display='flex' alignItems='center'>
              <Box width={36} height={36} display='flex' alignItems='center'>
                <Lottie
                  animationData={hourGlassAnimation}
                  loop={true}
                  style={{ width: '100%', height: '100%' }}
                />
              </Box>
              <Typography fontSize={'14px'}>
                <b>{diffInDays}</b> days left to post
              </Typography>
            </Box>
            {renderExtendButton()}
          </Box>
        );
      if (diffInDays === 1)
        return (
          <Box display='flex' alignItems='center' gap={'16px'} width='100%'>
            <Box display='flex' alignItems='center'>
              <Box width={36} height={36} display='flex' alignItems='center'>
                <Lottie
                  animationData={hourGlassAnimation}
                  loop={true}
                  style={{ width: '100%', height: '100%' }}
                />
              </Box>
              <Typography fontSize={'14px'}>
                <b>1</b> day left to post
              </Typography>
            </Box>
            {renderExtendButton()}
          </Box>
        );
      if (diffInHours > 1)
        return (
          <Box display='flex' alignItems='center' gap={'16px'} width='100%'>
            <Box display='flex' alignItems='center'>
              <Box width={36} height={36} display='flex' alignItems='center'>
                <Lottie
                  animationData={hourGlassAnimation}
                  loop={true}
                  style={{ width: '100%', height: '100%' }}
                />
              </Box>
              <Typography fontSize={'14px'}>
                <b>{diffInHours}</b> hours left to post
              </Typography>
            </Box>
            {renderExtendButton()}
          </Box>
        );
      if (diffInHours <= 1 && diffInHours > 0)
        return (
          <Box display='flex' alignItems='center' gap={'16px'} width='100%'>
            <Box display='flex' alignItems='center'>
              <Box width={36} height={36} display='flex' alignItems='center'>
                <Lottie
                  animationData={hourGlassAnimation}
                  loop={true}
                  style={{ width: '100%', height: '100%' }}
                />
              </Box>
              <Typography fontSize={'14px'}>
                <b>1</b> hour left to post
              </Typography>
            </Box>
            {renderExtendButton()}
          </Box>
        );
    }

    if (
      data?.status === ReservationStatus.checkedin &&
      data?.checkInDateTime &&
      !isProductCategory
    ) {
      const timeToAdd =
        ReservationPostTypePostingTimeLimitInHours[requirementsValues || 'noRequirement'];
      const targetDate = dayjs.utc(data?.effectiveEndDateTime)
      const now = dayjs.utc()
      const diffInHours = targetDate.diff(now, 'hour');
      const diffInDays = targetDate.diff(now, 'day');

    //   if (diffInHours <= 0 && diffInDays <= 0 && !data?.requirementsSubmitted)
    //     return <StatusLabelV2 type='promotionStatus' status={'notCompleted'} />;

    //   if (data?.requirementsSubmitted)
    //     return <StatusLabelV2 type='promotionStatus' status={'completed'} />;

      if (diffInDays > 1)
        return (
          <Box display='flex' alignItems='center' gap={'16px'} width='100%'>
            <Box display='flex' alignItems='center'>
              <Box width={36} height={36} display='flex' alignItems='center'>
                <Lottie
                  animationData={hourGlassAnimation}
                  loop={true}
                  style={{ width: '100%', height: '100%' }}
                />
              </Box>
              <Typography fontSize={'14px'}>
                <b>{Math.ceil(diffInHours / 24)}</b> days left to post
              </Typography>
            </Box>
            {renderExtendButton()}
          </Box>
        );
      if (diffInDays === 1)
        return (
          <Box display='flex' alignItems='center' gap={'16px'} width='100%'>
            <Box display='flex' alignItems='center'>
              <Box width={36} height={36} display='flex' alignItems='center'>
                <Lottie
                  animationData={hourGlassAnimation}
                  loop={true}
                  style={{ width: '100%', height: '100%' }}
                />
              </Box>
              <Typography fontSize={'14px'}>
                <b>1</b> day left to post
              </Typography>
            </Box>
            {renderExtendButton()}
          </Box>
        );
      if (diffInHours > 1)
        return (
          <Box display='flex' alignItems='center' gap={'16px'} width='100%'>
            <Box display='flex' alignItems='center'>
              <Box width={36} height={36} display='flex' alignItems='center'>
                <Lottie
                  animationData={hourGlassAnimation}
                  loop={true}
                  style={{ width: '100%', height: '100%' }}
                />
              </Box>
              <Typography fontSize={'14px'}>
                <b>{diffInHours}</b> hours left to post
              </Typography>
            </Box>
            {renderExtendButton()}
          </Box>
        );
      if (diffInHours <= 1 && diffInHours > 0)
        return (
          <Box display='flex' alignItems='center' gap={'16px'} width='100%'>
            <Box display='flex' alignItems='center'>
              <Box width={36} height={36} display='flex' alignItems='center'>
                <Lottie
                  animationData={hourGlassAnimation}
                  loop={true}
                  style={{ width: '100%', height: '100%' }}
                />
              </Box>
              <Typography fontSize={'14px'}>
                <b>1</b> hour left to post
              </Typography>
            </Box>
            {renderExtendButton()}
          </Box>
        );
    }

    return '-';
  }, [
    data?.status,
    data?.shippedDate,
    data?.checkInDateTime,
    data?.effectiveEndDateTime,
    data?.requirementsSubmitted,
    isProductCategory,
    requirementsValues,
    renderExtendButton,
  ]);

  return timeLeftToPost;
};
